import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import { Folder, File, ChevronRight, ChevronDown, RefreshCw, Download } from 'lucide-react';
import { toast } from 'react-hot-toast';

const FileNode = ({ item, level = 0 }) => {
    const { t } = useLanguage();
    const { formatDate } = useDateFormat();
    const [isOpen, setIsOpen] = useState(false);
    const paddingLeft = `${level * 20}px`;

    if (item.type === 'directory') {
        return (
            <div className="select-none">
                <div
                    className="flex items-center gap-2 py-2 px-2 hover:bg-slate-100 dark:hover:bg-slate-700/50 cursor-pointer rounded-md transition-colors"
                    style={{ paddingLeft }}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <div className="text-slate-400">
                        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </div>
                    <Folder className="text-blue-500 fill-current" size={18} />
                    <span className="font-medium text-sm text-slate-700 dark:text-slate-200">{item.name}</span>
                    <span className="text-xs text-slate-400 ml-2">({item.children?.length || 0})</span>
                    {item.updatedAt && (
                        <span className="ml-auto text-xs text-slate-400">
                            {formatDate(item.updatedAt)}
                        </span>
                    )}
                </div>
                {isOpen && item.children && (
                    <div className="border-l border-slate-200 dark:border-slate-700/50 ml-4">
                        {item.children.map((child, idx) => (
                            <FileNode key={idx} item={child} level={level + 1} />
                        ))}
                        {item.children.length === 0 && (
                            <div className="pl-6 text-xs text-slate-400 py-2 italic">
                                {t('files.empty_folder', 'Carpeta vacía')}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <a
            href={item.url}
            download={item.name}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 py-2 px-2 hover:bg-slate-100 dark:hover:bg-slate-700/50 cursor-pointer rounded-md group transition-colors ml-4"
            style={{ paddingLeft }}
        >
            <File className="text-slate-400 group-hover:text-blue-500 transition-colors" size={16} />
            <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 break-all">
                {item.name}
            </span>
            <div className="ml-auto flex items-center gap-4 text-xs text-slate-400">
                {item.updatedAt && (
                    <span>{formatDate(item.updatedAt)}</span>
                )}
                <span>{(item.size / 1024).toFixed(1)} KB</span>
                <Download size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
        </a>
    );
};

export const FileExplorerPage = () => {
    const { token } = useAuth();
    const { t } = useLanguage();
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);


    const fetchFiles = async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000); // 20s timeout

        try {
            setLoading(true);
            setError(null);

            console.log('[FileExplorer] Fetching files...');
            const res = await fetch('/api/files', {
                headers: { Authorization: `Bearer ${token}` },
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || `Error ${res.status}: ${res.statusText}`);
            }
            const data = await res.json();
            console.log('[FileExplorer] Files loaded:', data);
            setFiles(data);
        } catch (error) {
            console.error('[FileExplorer] Fetch Error:', error);
            if (error.name === 'AbortError') {
                setError(t('files.timeout_error', 'La solicitud tardó demasiado. Por favor, reintente.'));
            } else {
                setError(error.message);
            }
            toast.error(t('files.error_loading', 'Error al cargar la estructura de archivos'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchFiles();
        }
    }, [token]);

    useEffect(() => {
        console.log('[FileExplorer] Render:', { files: files?.length, loading, error, token: token ? 'Present' : 'Missing' });
    }, [files, loading, error, token]);

    return (
        <div className="container mx-auto px-4 py-8 max-w-6xl">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                        <Folder className="text-blue-600" />
                        {t('files.title', 'Explorador de Archivos')}
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 mt-1">
                        {t('files.subtitle', 'Visualiza y descarga los archivos almacenados en tu organización.')}
                    </p>
                </div>
                <button
                    onClick={fetchFiles}
                    className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-slate-600 dark:text-slate-300 font-medium text-sm"
                >
                    <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                    <span>{t('files.refresh', 'Actualizar')}</span>
                </button>
            </div>

            {/* Content Window */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden min-h-[400px]">
                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t('files.structure', 'Estructura del Directorio')}</span>
                    <span className="text-xs text-slate-400">{t('files.readonly', 'Solo lectura')}</span>
                </div>

                <div className="p-4 relative">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
                            <p>{t('files.scanning', 'Escaneando servidor...')}</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12 text-red-500 bg-red-50 rounded-lg border border-red-100 p-6 mx-auto max-w-md">
                            <div className="mb-4">⚠️</div>
                            <p className="font-bold mb-2">{t('files.error_loading', 'Error al cargar archivos')}</p>
                            <p className="text-sm font-mono mb-4 text-red-700 break-words">{error}</p>
                            <button
                                onClick={fetchFiles}
                                className="px-4 py-2 bg-white border border-red-200 rounded-lg text-red-600 hover:bg-red-50 text-sm font-semibold transition-colors shadow-sm"
                            >
                                {t('common.retry', 'Reintentar')}
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {files.length > 0 ? (
                                files.map((item, idx) => (
                                    <FileNode key={idx} item={item} />
                                ))
                            ) : (
                                <div className="text-center py-20 text-slate-400">
                                    <Folder size={64} className="mx-auto mb-4 opacity-10" />
                                    <p className="text-lg font-medium text-slate-500 mb-1">{t('files.no_files', 'No se encontraron archivos')}</p>
                                    <p className="text-sm text-slate-400">Su carpeta de organización está vacía.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Debug/Version Info */}
            <div className="text-right mt-4 text-[10px] text-slate-300 font-mono">
                v1.0.3 • {new Date().toISOString().split('T')[0]}
            </div>
        </div>
    );
};

export default FileExplorerPage;
